import React, { useCallback, useState } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildPromptSteps } from "@/types/flows";
import { ThumbnailContent } from "@/components/file/Carousel/ThumbnailContent";
import { useTextPromptMode } from "@/store";
import { useFrame } from "@/contexts/FrameContext";
import { MediaItemFileType } from "@/types/media";

export interface Thumbnail {
    thumbnailDataUrl: string;
    id: string;
    fileId?: string;
    frameId?: string | number; // uuid for annotations, segment for predictions
}

interface CardThumbnailsProps {
    name: string;
    mode: BuildPromptSteps;
    thumbnails: Thumbnail[];
    selectedObject: string | null;
    previouslySelectedObject: string | null;
    flowLocked: boolean;
    onThumbnailClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onDeleteAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragStart?: (e: React.DragEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragEnd?: () => void;
    labelColor?: string;
}

export const CardThumbnails: React.FC<CardThumbnailsProps> = ({
    name,
    mode,
    thumbnails,
    selectedObject,
    previouslySelectedObject,
    flowLocked,
    onThumbnailClick,
    onDeleteAnnotationClick,
    onThumbnailDragStart,
    onThumbnailDragEnd,
    labelColor
}) => {
    const [hoveredThumbnailId, setHoveredThumbnailId] = useState<string | null>(null);
    const textPromptMode = useTextPromptMode();
    const { setHoveredAnnotationId } = useFrame();

    const handleThumbnailMouseEnter = useCallback(
        (thumbnail: Thumbnail) => {
            setHoveredThumbnailId(thumbnail.id);
            // Always set the hovered annotation ID to enable canvas highlighting
            // The canvas will only show/highlight it if it's on the current frame
            setHoveredAnnotationId(thumbnail.id);
        },
        [setHoveredAnnotationId]
    );

    const handleThumbnailMouseLeave = useCallback(() => {
        setHoveredThumbnailId(null);
        setHoveredAnnotationId(null);
    }, [setHoveredAnnotationId]);

    return (
        <div className="flex w-full gap-2 overflow-x-auto px-4 py-1">
            {thumbnails.length > 0 ? (
                <>
                    {thumbnails.map(
                        (thumbnail, index) =>
                            thumbnail &&
                            thumbnail.thumbnailDataUrl && (
                                <div
                                    key={`${name}-thumb-wrapper-${index}-${thumbnail.id}-${thumbnail.fileId}`}
                                    onMouseEnter={() => handleThumbnailMouseEnter(thumbnail)}
                                    onMouseLeave={handleThumbnailMouseLeave}
                                    onDragStart={(e) => onThumbnailDragStart?.(e, thumbnail)}
                                    onDragEnd={onThumbnailDragEnd}
                                    draggable={!flowLocked}
                                >
                                    <ThumbnailContent
                                        thumbnail={thumbnail.thumbnailDataUrl}
                                        isUploading={false}
                                        fileType={MediaItemFileType.IMAGE}
                                        fileName="annotation"
                                        title="View annotation"
                                        className={twClassNames(
                                            mode === BuildPromptSteps.REVIEW_PREDICTIONS &&
                                                "border-dashed",
                                            flowLocked
                                                ? "pointer-events-none opacity-100"
                                                : "cursor-pointer"
                                        )}
                                        onClick={(e) => onThumbnailClick?.(e, thumbnail)}
                                        onDelete={
                                            hoveredThumbnailId === thumbnail.id &&
                                            !textPromptMode &&
                                            onDeleteAnnotationClick
                                                ? (e) => onDeleteAnnotationClick(e, thumbnail)
                                                : undefined
                                        }
                                        isShareMode={false}
                                        flowLocked={flowLocked}
                                        showOverlay={!flowLocked}
                                    />
                                </div>
                            )
                    )}
                </>
            ) : (
                !(flowLocked && mode !== BuildPromptSteps.REVIEW_PREDICTIONS) && (
                    <div
                        className={twClassNames(
                            "fileThumbnail border border-dashed border-gray-300"
                        )}
                        style={
                            mode === BuildPromptSteps.ANNOTATE_DATA &&
                            !textPromptMode &&
                            (selectedObject === name || previouslySelectedObject === name)
                                ? {
                                      backgroundColor: `${labelColor}1A`,
                                      borderColor: labelColor,
                                      color: labelColor
                                  }
                                : undefined
                        }
                    >
                        {(mode === BuildPromptSteps.REVIEW_PREDICTIONS ||
                            (!textPromptMode && mode === BuildPromptSteps.ANNOTATE_DATA)) && (
                            <span className="text-center text-xs">
                                {mode === BuildPromptSteps.ANNOTATE_DATA ? (
                                    <>
                                        Draw <br /> a box
                                    </>
                                ) : mode === BuildPromptSteps.REVIEW_PREDICTIONS ? (
                                    <>
                                        Adjust <br /> slider
                                    </>
                                ) : null}
                            </span>
                        )}
                    </div>
                )
            )}
        </div>
    );
};
